import type { BusinessSegmentConditions, SegmentConditionGroups } from '@reach/shared-types/conditions';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { appRoutes, buildSegmentEditPath } from '../../../app-routes';
import {
  getTenantSelectionType,
  toTargetedTenantsFilter,
  type TenantSelectionType,
} from '../engage-automations-tab/templates.types';
import { isLegacySegmentFormat } from '../engage-segments-tab/segment-definitions-api';
import {
  useAvailableFields,
  useCreateSegmentFromTemplate,
  useSegmentDefinition,
  useUpdateSegment,
} from '../engage-segments-tab/useSegmentTemplates';
import styles from './EditSegment.module.css';
import { EditSegmentHeader } from './EditSegmentHeader';
import { SegmentDefinitionSection } from './SegmentDefinitionSection';
import { SegmentNameDescription } from './SegmentNameDescription';
import { parseConditionGroupsJson, parseLegacyConditionsJson } from './segmentDefinitionJson';
import { useSegmentTenantSelection } from './useSegmentTenantSelection';

const DEFAULT_AVAILABLE_FIELDS = {
  contactFields: [] as string[],
  relatedSchemas: [],
};

export function EditSegmentPage() {
  const { canonicalVersionId } = useParams<{ canonicalVersionId: string }>();
  const navigate = useNavigate();

  // Determine if we're in create mode (no canonicalVersionId) or edit mode
  const isCreateMode = !canonicalVersionId;

  // Only fetch segment data in edit mode
  const {
    data: segment,
    isLoading: isLoadingSegment,
    error: segmentError,
  } = useSegmentDefinition(canonicalVersionId);

  const {
    data: availableFields = DEFAULT_AVAILABLE_FIELDS,
    isLoading: isLoadingFields,
    error: fieldsError,
  } = useAvailableFields();

  // Update mutation for edit mode
  const { mutate: updateSegment, isPending: isUpdating } = useUpdateSegment(canonicalVersionId || '', {
    onSuccess: () => {
      setSavedTenantSelectionType(tenantSelectionType);
      setSavedSelectedTenantIds(selectedTenantIds);
    },
  });

  // Create mutation for create mode
  const createSegmentMutation = useCreateSegmentFromTemplate({
    onSuccess: newCanonicalVersionId => {
      // After creating, navigate to edit mode for the new segment
      navigate(buildSegmentEditPath(newCanonicalVersionId), { replace: true });
    },
  });

  const {
    tenantRowData,
    conflicts,
    isLoading: isLoadingTenants,
  } = useSegmentTenantSelection({
    protectedTenantIds: segment?.businessIdsWithSegmentInActiveAutomation ?? [],
    conflictingAutomationNamesByBusinessId: segment?.conflictingAutomationNamesByBusinessId ?? {},
  });

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [contentsJson, setContentsJson] = useState('[]');
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [editorMode, setEditorMode] = useState<'builder' | 'json' | 'preview'>('builder');
  const [formatType, setFormatType] = useState<'legacy' | 'new'>('new');
  const [tenantSelectionType, setTenantSelectionType] = useState<TenantSelectionType>('all');
  const [selectedTenantIds, setSelectedTenantIds] = useState<string[]>([]);
  const [savedTenantSelectionType, setSavedTenantSelectionType] = useState<TenantSelectionType>('all');
  const [savedSelectedTenantIds, setSavedSelectedTenantIds] = useState<string[]>([]);

  // Initialize form when segment loads (edit mode only)
  useEffect(() => {
    if (segment && !isCreateMode) {
      setName(segment.name);
      setDescription(segment.description || '');
      const loadedType = getTenantSelectionType(segment.targetedTenantsFilter);
      const loadedIds = segment.targetedTenantsFilter ?? [];
      setTenantSelectionType(loadedType);
      setSelectedTenantIds(loadedIds);
      setSavedTenantSelectionType(loadedType);
      setSavedSelectedTenantIds(loadedIds);

      // Legacy = contents (conditions) exist AND condition_groups is empty or not provided.
      const legacy = isLegacySegmentFormat({
        conditions: segment.contents,
        conditionGroups: segment.conditionGroups,
      });

      if (legacy) {
        setFormatType('legacy');
        setContentsJson(JSON.stringify(segment.contents, null, 2));
        setEditorMode('json');
      } else {
        setFormatType('new');
        const groups = segment.conditionGroups ?? [];
        setContentsJson(JSON.stringify(groups, null, 2));
      }
    }
  }, [segment, isCreateMode]);

  const parsedConditions = useMemo((): BusinessSegmentConditions | null => {
    if (formatType !== 'legacy' || !contentsJson) return null;
    return parseLegacyConditionsJson(contentsJson, isJsonValid);
  }, [contentsJson, isJsonValid, formatType]);

  const parsedConditionGroups = useMemo((): SegmentConditionGroups | null => {
    if (formatType !== 'new' || !contentsJson) return null;
    return parseConditionGroupsJson(contentsJson);
  }, [contentsJson, formatType]);

  const conditionGroupsForPreview =
    formatType === 'new' && editorMode === 'builder'
      ? parsedConditionGroups
      : formatType === 'new' && isJsonValid && parsedConditionGroups
        ? parsedConditionGroups
        : null;

  const isTenantSelectionDirty =
    tenantSelectionType !== savedTenantSelectionType ||
    (tenantSelectionType === 'selected' &&
      (selectedTenantIds.length !== savedSelectedTenantIds.length ||
        [...selectedTenantIds].sort().join(',') !== [...savedSelectedTenantIds].sort().join(',')));

  const handleTenantSelectionChange = useCallback((type: TenantSelectionType, ids: string[]) => {
    setTenantSelectionType(type);
    setSelectedTenantIds(ids);
  }, []);

  const tenantFilterParam = {
    targetedTenantsFilter: toTargetedTenantsFilter(tenantSelectionType, selectedTenantIds),
  };

  const handleSave = () => {
    if (!isJsonValid) return;

    if (formatType === 'legacy') {
      const contents = parseLegacyConditionsJson(contentsJson, isJsonValid);
      if (contents == null) return;
      if (isCreateMode) {
        createSegmentMutation.mutate({
          name,
          description,
          contents,
          ...tenantFilterParam,
        });
      } else {
        updateSegment({
          name,
          description,
          contents,
          conditionGroups: null,
          ...tenantFilterParam,
        });
      }
    } else {
      const groups = parseConditionGroupsJson(contentsJson) ?? [];

      if (isCreateMode) {
        createSegmentMutation.mutate({
          name,
          description,
          conditionGroups: groups,
          ...tenantFilterParam,
        });
      } else {
        updateSegment({
          name,
          description,
          conditionGroups: groups,
          ...tenantFilterParam,
        });
      }
    }
  };

  const navigateToSegmentsList = () => {
    navigate(appRoutes.ENGAGE_SEGMENTS);
  };

  // Loading state - only show in edit mode when fetching existing segment
  if (!isCreateMode && isLoadingSegment) {
    return <p className={styles.loadingState}>Loading segment...</p>;
  }

  // Error state - only show in edit mode
  if (!isCreateMode && (segmentError || !segment)) {
    return <p className={styles.errorState}>Error loading segment. Please try again.</p>;
  }

  const isSaving = isCreateMode ? createSegmentMutation.isPending : isUpdating;

  const isSaveDisabled =
    isSaving ||
    isLoadingFields ||
    !name.trim() ||
    !description.trim() ||
    (editorMode === 'json' && !isJsonValid);

  return (
    <article className={styles.page}>
      <EditSegmentHeader
        onBack={navigateToSegmentsList}
        onSave={handleSave}
        isSaveDisabled={isSaveDisabled}
        isSaving={isSaving}
        saveLabel={isCreateMode ? 'Create Segment' : 'Save Segment'}
        tenantRowData={tenantRowData}
        conflicts={conflicts}
        tenantSelectionType={tenantSelectionType}
        selectedTenantIds={selectedTenantIds}
        onTenantSelectionChange={handleTenantSelectionChange}
        isTenantSelectionDirty={isTenantSelectionDirty}
        isLoadingTenants={isLoadingTenants}
      />

      <main>
        <SegmentNameDescription
          name={name}
          description={description}
          onNameChange={setName}
          onDescriptionChange={setDescription}
          disabled={isSaving}
          defaultEditing={isCreateMode}
        />

        <SegmentDefinitionSection
          formatType={formatType}
          editorMode={editorMode}
          onEditorModeChange={setEditorMode}
          contentsJson={contentsJson}
          onContentsChange={setContentsJson}
          onValidationChange={setIsJsonValid}
          isJsonValid={isJsonValid}
          isSaving={isSaving}
          availableFields={availableFields}
          isLoadingFields={isLoadingFields}
          fieldsError={fieldsError}
          parsedConditions={parsedConditions}
          parsedConditionGroups={parsedConditionGroups}
          conditionGroupsForPreview={conditionGroupsForPreview}
        />
      </main>
    </article>
  );
}

export default EditSegmentPage;
